<template>
  <div class="TaskReports">
    <!-- Modern header similar to TodoDetails.vue -->
    <div class="details-header">
      <h4 class="details-title">Task Reports</h4>
      <div class="header-actions">
        <button class="export-button" @click="exportReportToCSV" aria-label="Export Report to CSV">
          <i class="fa fa-download" aria-hidden="true"></i> Export CSV
        </button>
        <a href="#" @click.prevent="closeSlideOutPanel" title="Close" class="close-button" aria-label="Close Task Reports Panel">
          <i class="fa fa-times" aria-hidden="true"></i>
        </a>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status" aria-label="Loading Task Reports">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Filter Summary Text -->
    <div class="filter-summary-container">
      <p class="filter-summary-text">
        <i class="fa fa-filter" aria-hidden="true"></i>
        {{ filterSummaryText }}
      </p>
    </div>

    <!-- Compact Filters Row (matching main task page layout) -->
    <div class="task-filters">
      <div class="filters-container">
        <!-- Date Range Filter -->
        <div class="filter-group">
          <DatePicker
            :range="true"
            lang="en"
            v-model="dateRange"
            :format="dateFormatStr"
            placeholder="Filter by Due Date"
            input-class="filter-date-input"
            append-to-body
            aria-label="Select Date Range for Task Reports">
          </DatePicker>
        </div>

        <!-- Tag Filter -->
        <div class="filter-group">
          <Multiselect
            v-model="selectedTagsFilter"
            :options="availableTagsInTasks"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :show-labels="false"
            placeholder="Filter by tags"
            label="name"
            track-by="name"
            :taggable="false"
            class="filter-select"
            aria-label="Select tags to filter task reports">
            <template slot="option" slot-scope="{ option }">
              <div class="tag-option">
                <i class="fa fa-tag"></i>
                {{ option.name }}
              </div>
            </template>
            <template slot="selection" slot-scope="{ values, isOpen }">
              <span class="multiselect__single" v-if="values.length && !isOpen">
                {{ values.map(v => v.name).join(', ') }}
              </span>
            </template>
          </Multiselect>
        </div>

        <!-- Clear Filters Button -->
        <div class="filter-group" v-if="selectedTagsFilter.length > 0">
          <button 
            @click="clearTagFilter" 
            class="clear-filters-button"
            aria-label="Clear tag filter">
            <i class="fa fa-times" aria-hidden="true"></i>
            Clear Tags
          </button>
        </div>
      </div>
    </div>

    <!-- Metrics Overview Section -->
    <div class="task-details-item metrics-overview-container" v-if="allTasksForSite.length > 0">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-chart-line section-icon" aria-hidden="true"></i>
          Performance Metrics
        </h5>
      </div>
      <div class="metrics-cards">
        <!-- On-time Completion Rate -->
        <div class="metric-card" @click="setMetricFilter('onTime')" :class="{'metric-selected': selectedMetricFilter === 'onTime'}" role="button" tabindex="0" aria-label="Filter by On-time Completion Rate" @keydown.enter="setMetricFilter('onTime')" @keydown.space="setMetricFilter('onTime')">
          <div class="metric-icon">
            <i class="fa fa-check-circle" aria-hidden="true"></i>
          </div>
          <div class="metric-content">
            <h6 class="metric-title">On-time Completion</h6>
            <div class="metric-value">{{ onTimeCompletionRate }}%</div>
            <div class="metric-trend" :class="onTimeCompletionTrend.class">
              <i :class="onTimeCompletionTrend.icon" aria-hidden="true"></i>
              {{ onTimeCompletionTrend.text }}
            </div>
          </div>
        </div>

        <!-- Average Completion Time -->
        <div class="metric-card" @click="setMetricFilter('completionTime')" :class="{'metric-selected': selectedMetricFilter === 'completionTime'}" role="button" tabindex="0" aria-label="Filter by Average Completion Time" @keydown.enter="setMetricFilter('completionTime')" @keydown.space="setMetricFilter('completionTime')">
          <div class="metric-icon">
            <i class="fa fa-clock" aria-hidden="true"></i>
          </div>
          <div class="metric-content">
            <h6 class="metric-title">Avg. Completion Time</h6>
            <div class="metric-value">{{ avgCompletionTime }} days</div>
            <div class="metric-trend" :class="avgCompletionTimeTrend.class">
              <i :class="avgCompletionTimeTrend.icon" aria-hidden="true"></i>
              {{ avgCompletionTimeTrend.text }}
            </div>
          </div>
        </div>

        <!-- Task Volume -->
        <div class="metric-card" role="button" tabindex="0" aria-label="Task Volume Information">
          <div class="metric-icon">
            <i class="fa fa-tasks" aria-hidden="true"></i>
          </div>
          <div class="metric-content">
            <h6 class="metric-title">Task Volume</h6>
            <div class="metric-value">{{ totalTasksInRange }}</div>
            <div class="metric-trend">
              <span>In selected period</span>
            </div>
          </div>
        </div>

        <!-- Incomplete Tasks -->
        <div class="metric-card" @click="setMetricFilter('incomplete')" :class="{'metric-selected': selectedMetricFilter === 'incomplete'}" role="button" tabindex="0" aria-label="Filter by Incomplete Tasks" @keydown.enter="setMetricFilter('incomplete')" @keydown.space="setMetricFilter('incomplete')">
          <div class="metric-icon">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
          </div>
          <div class="metric-content">
            <h6 class="metric-title">Incomplete Tasks</h6>
            <div class="metric-value">{{ incompleteTasksCount }}</div>
            <div class="metric-trend" :class="incompleteTrend.class">
              <i :class="incompleteTrend.icon" aria-hidden="true"></i>
              {{ incompleteTrend.text }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Employee Performance Scorecard -->
    <div class="task-details-item" v-if="allTasksForSite.length > 0">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-users section-icon" aria-hidden="true"></i>
          Employee Performance Scorecard
        </h5>
        <div class="chart-controls">
          <label for="sortBy" class="control-label">Sort by:</label>
          <select id="sortBy" v-model="scorecardSortBy" class="form-control" aria-label="Select Sorting Criteria for Employee Performance">
            <option value="totalTasks">Total Tasks</option>
            <option value="completionRate">Completion Rate</option>
            <option value="onTimeRate">On-Time Rate</option>
            <option value="avgCompletionTime">Avg. Completion Time</option>
          </select>
          <label for="sortOrder" class="control-label">Order:</label>
          <select id="sortOrder" v-model="scorecardSortOrder" class="form-control" aria-label="Select Sorting Order for Employee Performance">
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>
      <div class="task-details-item-content p-3 scorecard-container">
        <div class="table-responsive">
          <table class="performance-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Total Tasks</th>
                <th>Completion Rate</th>
                <th>On-Time Rate</th>
                <th>Avg. Completion Time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(employeeData, empId) in sortedScorecardData" :key="`scorecard-${empId}`">
                <td>
                  <div class="employee-info">
                    <div
                      class="employee-avatar"
                      :class="getAvatarClass(empId)"
                      :style="getPersonAvatar(empId) ? `background-image: url(${getPersonAvatar(empId)})` : ''"
                      :aria-label="!getPersonAvatar(empId) ? `Avatar for ${getEmployeeName(empId)}` : `Profile picture for ${getEmployeeName(empId)}`"
                    >
                      {{ !getPersonAvatar(empId) ? getInitials(getEmployeeName(empId)) : '' }}
                    </div>
                    <span class="employee-name-cell">{{ getEmployeeName(empId) }}</span>
                  </div>
                </td>
                <td>{{ employeeData.totalTasks }}</td>
                <td>{{ calculateCompletionRate(employeeData) }}%</td>
                <td>{{ calculateOnTimeRate(employeeData) }}%</td>
                <td>{{ calculateAvgCompletionTime(employeeData) }} days</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Employee reports in modern cards -->
    <div class="reports-container">
      <div v-if="allTasksForSite.length > 0" class="employee-reports-grid">
        <div
          v-for="(employee, employeeID) in report"
          :key="`employee-${employeeID}`"
          class="employee-report-card">
          <div class="employee-card-header">
            <div
              class="employee-avatar"
              :class="getAvatarClass(employeeID)"
              :style="getPersonAvatar(employeeID) ? `background-image: url(${getPersonAvatar(employeeID)})` : ''"
              :aria-label="!getPersonAvatar(employeeID) ? `Avatar for ${getEmployeeName(employeeID)}` : `Profile picture for ${getEmployeeName(employeeID)}`"
            >
              {{ !getPersonAvatar(employeeID) ? getInitials(getEmployeeName(employeeID)) : '' }}
            </div>
            <h6 class="employee-name">{{ getEmployeeName(employeeID) }}</h6>
          </div>
          <div class="chart-panel">
            <PieChart
              v-if="makePieChartData(employee)"
              :chartdata="makePieChartData(employee)"
              :options="chartOptions"
              :styles="chartStyles"
              :key="Math.round(+new Date()/1000)"
              ref="pie"
              role="img"
              :aria-label="`Task completion breakdown for ${getEmployeeName(employeeID)}`"/>
          </div>
        </div>
      </div>
      <div v-else class="no-data-message">
        <p>No task data available for the selected date range.</p>
      </div>
    </div>

    <!-- Task Distribution Chart -->
    <div class="task-details-item" v-if="allTasksForSite.length > 0">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-chart-bar section-icon" aria-hidden="true"></i>
          Task Distribution
        </h5>
        <div class="chart-controls">
          <label for="employeeCount" class="control-label">Show:</label>
          <select id="employeeCount" v-model="displayEmployeeCount" class="form-control" aria-label="Select Number of Employees to Display">
            <option :value="5">5 Employees</option>
            <option :value="10">10 Employees</option>
            <option :value="15">15 Employees</option>
            <option :value="20">20 Employees</option>
          </select>
        </div>
      </div>
      <div class="task-details-item-content p-3">
        <div class="chart-container">
          <canvas ref="taskDistributionChart" height="250" role="img" aria-label="Task Distribution Chart showing completed and incomplete tasks by employee"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Multiselect from "vue-multiselect";
import _ from 'lodash';
import Widget from '@/components/Widget/Widget';
import moment from 'moment-timezone';
import DatePicker from "vue2-datepicker";
import { mapState, mapActions } from 'vuex';
import mixin from '../../../mixin.js';
import InsightMSBarChart from '@/pages/TeamInsights/components/InsightMSBarChart';
import PieChart from '@/pages/TeamInsights/components/InsightPieChart';
import Chart from 'chart.js';

  export default {
    name: 'TaskReports',
    components: { Multiselect, DatePicker, Widget, InsightMSBarChart, PieChart },
    //props: ['detailType', 'detailData'],
    mixins: [mixin],

    mounted() {
      $(window).trigger('resize');
      this.$nextTick(() => {
        this.renderTaskDistributionChart();
      });
    },

    created() {
      this.init();
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },

    data() {
      return {
        dateRange: [new Date(moment().startOf('week')), new Date(moment().endOf('week'))], // Default to current week (Monday to Sunday)
        windowWidth: window.innerWidth,
        avatarColors: [
          'bg-blue-100 text-blue-800',
          'bg-green-100 text-green-800',
          'bg-yellow-100 text-yellow-800',
          'bg-red-100 text-red-800',
          'bg-indigo-100 text-indigo-800',
          'bg-purple-100 text-purple-800',
          'bg-pink-100 text-pink-800',
          'bg-teal-100 text-teal-800'
        ],
        avatarLoadError: {},
        chartOptions: {
          layout: {
              padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0
              }
          },
          legend: {
            display: true,
            position: window.innerWidth < 3400 ? 'bottom' : 'right',
            fullWidth: true,
            labels:{
              padding: 10,
              boxWidth: 15,
              labelWidth: 10
              // margin: 10
            }
          },
          responsive: true,
          maintainAspectRatio: false
        },
        report: {},
        // For metrics overview
        previousPeriodData: null,
        barChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true
            }
          },
          plugins: {
            legend: {
              position: 'bottom'
            }
          }
        },
        debounceTimer: null,
        displayEmployeeCount: 10, // Default number of employees to display in Task Distribution Chart
        trendGranularity: 'daily', // Default granularity for Task Completion Trend Chart
        selectedMetricFilter: null, // To track which metric is selected for filtering charts
        scorecardSortBy: 'totalTasks', // Default sorting criteria for scorecard
        scorecardSortOrder: 'desc', // Default sorting order for scorecard
        selectedTagsFilter: [] // Array of selected tag objects for filtering
      };
    },

  methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
      ...mapActions('tasks', ['getTaskReports']),
      ...mapActions('practicepulse', ['getPracticePulseReport']),

      async init() {
        try {
          await this.getTaskReports({siteID: this.siteID});
          // Initialize report with default date range (this week)
          if (this.allTasksForSite && this.allTasksForSite.length > 0) {
            this.report = this.generateReport(
              this.allTasksForSite,
              moment(this.dateRange[0]),
              moment(this.dateRange[1]),
              this.selectedTagsFilter
            );
            this.$nextTick(() => {
              console.log('Initializing Task Distribution Chart with report data:', this.report);
              this.renderTaskDistributionChart();
            });
          }
        } catch (error) {
          this.setErrored(true);
          this.setMessage('Error loading task reports: ' + error.message);
        }
      },

      clearTagFilter() {
        this.selectedTagsFilter = [];
      },

      removeTag(tag) {
        const index = this.selectedTagsFilter.findIndex(t => t.name === tag.name);
        if (index > -1) {
          this.selectedTagsFilter.splice(index, 1);
        }
      },

      async closeSlideOutPanel() {
        this.$emit('closePanel');
      },

      onResize() {
        this.windowWidth = window.innerWidth;
      },

      getEmployeePic(employeeID) {
        if(this.employees) {
          let users = this.employees.filter(e => e.EmployeeID === employeeID);
          if(users.length > 0)
            return users[0].PictureUrl;
        }
      },

      getInitials(name) {
        if (!name) return '';

        const parts = name.split(' ');
        if (parts.length === 1) return parts[0].charAt(0);

        return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      },

      getPersonAvatar(employeeID) {
        if (this.employees) {
          const employee = this.employees.find(e => e.EmployeeID === employeeID);
          // Check for valid PictureUrl - must exist, not be "null" string, and not be empty
          if (employee && employee.PictureUrl && 
              employee.PictureUrl !== 'null' && 
              employee.PictureUrl !== 'undefined' &&
              employee.PictureUrl.trim() !== '') {
            return employee.PictureUrl;
          }
        }
        return null;
      },

      getAvatarClass(employeeID) {
        if (this.getPersonAvatar(employeeID)) {
          return '';
        }

        if (!employeeID) return 'bg-gray-200 text-gray-700';

        let hash = 0;
        for (let i = 0; i < employeeID.length; i++) {
          hash = employeeID.charCodeAt(i) + ((hash << 5) - hash);
        }

        const colorIndex = Math.abs(hash) % this.avatarColors.length;
        return this.avatarColors[colorIndex];
      },

      renderTaskDistributionChart() {
        if (!this.$refs.taskDistributionChart || !this.report || Object.keys(this.report).length === 0) {
          console.log('Cannot render Task Distribution Chart: Missing ref or data', {
            hasRef: !!this.$refs.taskDistributionChart,
            hasReport: !!this.report,
            reportKeys: this.report ? Object.keys(this.report).length : 0
          });
          return;
        }

        console.log('Rendering Task Distribution Chart with data:', this.report);

        // Destroy existing chart if it exists
        if (this.taskDistributionChart) {
          this.taskDistributionChart.destroy();
        }

        const ctx = this.$refs.taskDistributionChart.getContext('2d');

        // Prepare data for the chart
        const employeeNames = [];
        const completedTasks = [];
        const incompleteTasks = [];
        const employeeAvatars = [];

        // Sort employees by total tasks (descending)
        const sortedEmployees = Object.entries(this.report)
          .map(([empId, data]) => ({ empId, data }))
          .sort((a, b) => (b.data.totalTasks - a.data.totalTasks));

        // Take the number of employees based on user selection for better visualization
        const topEmployees = sortedEmployees.slice(0, this.displayEmployeeCount);

        topEmployees.forEach(({ empId, data }) => {
          employeeNames.push(this.getEmployeeName(empId));
          
          // Apply filter based on selected metric
          if (this.selectedMetricFilter === 'onTime') {
            completedTasks.push(data.onTime);
            incompleteTasks.push(0);
          } else if (this.selectedMetricFilter === 'incomplete') {
            completedTasks.push(0);
            incompleteTasks.push(data.incomplete);
          } else if (this.selectedMetricFilter === 'completionTime') {
            completedTasks.push(data.completedTasks);
            incompleteTasks.push(0);
          } else {
            completedTasks.push(data.completedTasks);
            incompleteTasks.push(data.incomplete);
          }

          // Get employee avatar
          const avatarUrl = this.getPersonAvatar(empId);
          employeeAvatars.push({
            url: avatarUrl,
            empId: empId
          });
        });

        // Create the chart
        this.taskDistributionChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: employeeNames,
            datasets: [
              {
                label: this.selectedMetricFilter === 'onTime' ? 'On Time Tasks' : this.selectedMetricFilter === 'completionTime' ? 'Completed Tasks' : 'Completed Tasks',
                data: completedTasks,
                backgroundColor: '#3abf94',
                borderColor: '#2d9a77',
                borderWidth: 1
              },
              {
                label: 'Incomplete Tasks',
                data: incompleteTasks,
                backgroundColor: '#64748b',
                borderColor: '#475569',
                borderWidth: 1,
                hidden: this.selectedMetricFilter === 'onTime' || this.selectedMetricFilter === 'completionTime'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                stacked: this.selectedMetricFilter ? false : true,
                grid: {
                  display: false
                }
              },
              y: {
                stacked: this.selectedMetricFilter ? false : true,
                beginAtZero: true,
                ticks: {
                  precision: 0
                }
              }
            },
            plugins: {
              legend: {
                position: 'top',
              },
              tooltip: {
                callbacks: {
                  title: function(tooltipItems) {
                    return tooltipItems[0].label;
                  },
                  label: function(context) {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    return `${label}: ${value}`;
                  }
                }
              }
            }
          }
        });

        // Add employee avatars to the chart
        const chartContainer = this.$refs.taskDistributionChart.parentNode;
        const existingAvatars = chartContainer.querySelectorAll('.employee-avatar-container');

        // Remove existing avatars if any
        existingAvatars.forEach(avatar => avatar.remove());

        // Create avatar container
        const avatarContainer = document.createElement('div');
        avatarContainer.className = 'employee-avatar-container';
        avatarContainer.style.display = 'flex';
        avatarContainer.style.justifyContent = 'space-around';
        avatarContainer.style.marginTop = '10px';
        avatarContainer.style.height = '30px';

        // Add avatars for each employee
        employeeAvatars.forEach((avatar) => {
          const avatarElement = document.createElement('div');
          avatarElement.className = 'chart-employee-avatar';

          if (avatar.url) {
            // If avatar URL exists, use it
            avatarElement.style.backgroundImage = `url(${avatar.url})`;
            avatarElement.style.backgroundSize = 'cover';
            avatarElement.style.backgroundPosition = 'center';
          } else {
            // Otherwise, use initials with color
            avatarElement.className += ' ' + this.getAvatarClass(avatar.empId);
            avatarElement.textContent = this.getInitials(this.getEmployeeName(avatar.empId));
          }

          avatarContainer.appendChild(avatarElement);
        });

        // Append avatar container after the chart
        chartContainer.appendChild(avatarContainer);
      },

      calculateCompletionRate(employeeData) {
        if (employeeData.totalTasks === 0) return 0;
        return Math.round((employeeData.completedTasks / employeeData.totalTasks) * 100);
      },

      calculateOnTimeRate(employeeData) {
        if (employeeData.completedTasks === 0) return 0;
        return Math.round((employeeData.onTime / employeeData.completedTasks) * 100);
      },

      calculateAvgCompletionTime(employeeData) {
        if (employeeData.completedTasks === 0) return 0;
        return (employeeData.totalCompletionDays / employeeData.completedTasks).toFixed(1);
      },

      makePieChartData(employeeID) {
        //console.log('in makePieChartData', employeeID);
        if(this.allTasksForSite.length > 0) {
          return {
            labels: ['On Time','1 Day Late','2 Days Late','3 Days Late','4 Days Late','5 Days Late','6 Days Late','Over 1 week Late','Incomplete'],
            datasets: [
              {
                label: "Data One",
                // Teamio Huddle-style color palette - consistent across all charts
                backgroundColor: ['#3abf94', '#3b82f6', '#8b5cf6', '#f59e0b', '#64748b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#78716c'],
                data: [employeeID.onTime,employeeID.late1Day,employeeID.late2Days,employeeID.late3Days,
                      employeeID.late4Days,employeeID.late5Days,employeeID.late6Days,employeeID.lateWeekOrMore,
                      employeeID.incomplete]
              }
            ]
          }
        }
      },

      generateReport(tasks, startDate, endDate, filterTags = []) {
          const report = {};

          console.log(`Generating report from ${startDate.format('MM/DD/YYYY')} to ${endDate.format('MM/DD/YYYY')}`);
          if (filterTags.length > 0) {
            console.log(`Filtering by tags: ${filterTags.map(t => t.name).join(', ')}`);
          }

          // Store the previous period data before generating new report
          const previousStartDate = moment(startDate).subtract(endDate.diff(startDate, 'days'), 'days');
          const previousEndDate = moment(startDate).subtract(1, 'day');
          this.previousPeriodData = this.calculatePreviousPeriodMetrics(tasks, previousStartDate, previousEndDate, filterTags);

          tasks.forEach((task) => {
            //console.log('looking at task', task);
            const taskDate = moment(task.dueAtStr);

            if (taskDate.isAfter(startDate) && taskDate.isBefore(endDate)) {
                // Filter by tags if any are selected
                if (filterTags.length > 0) {
                  const taskTags = Array.isArray(task.tags) ? task.tags : [];
                  const hasMatchingTag = filterTags.some(filterTag => 
                    taskTags.includes(filterTag.name)
                  );
                  
                  if (!hasMatchingTag) {
                    return; // Skip this task if it doesn't have any of the selected tags
                  }
                }
                const employeeId = task.assignedToEmpID;
                const dueDate = new Date(task.dueAtStr);
                const completionDate = new Date(task.completedAtStr);

                if (!report[employeeId]) {
                    report[employeeId] = {
                        onTime: 0,
                        late1Day: 0,
                        late2Days: 0,
                        late3Days: 0,
                        late4Days: 0,
                        late5Days: 0,
                        late6Days: 0,
                        lateWeekOrMore: 0,
                        incomplete: 0,
                        totalTasks: 0,
                        completedTasks: 0,
                        totalCompletionDays: 0 // For calculating average completion time
                    };
                }

                // Increment total tasks count
                report[employeeId].totalTasks += 1;

                if (task.status !== 'COMPLETED') {
                    report[employeeId].incomplete += 1;
                } else {
                    // Increment completed tasks count
                    report[employeeId].completedTasks += 1;

                    const daysAfterDue = Math.floor((completionDate - dueDate) / (1000 * 60 * 60 * 24));

                    // Add to total completion days for average calculation
                    report[employeeId].totalCompletionDays += Math.max(0, daysAfterDue);

                    if (daysAfterDue <= 0) {
                        report[employeeId].onTime += 1;
                    } else if (daysAfterDue === 1) {
                        report[employeeId].late1Day += 1;
                    } else if (daysAfterDue === 2) {
                        report[employeeId].late2Days += 1;
                    } else if (daysAfterDue === 3) {
                        report[employeeId].late3Days += 1;
                    } else if (daysAfterDue === 4) {
                        report[employeeId].late4Days += 1;
                    } else if (daysAfterDue === 5) {
                        report[employeeId].late5Days += 1;
                    } else if (daysAfterDue === 6) {
                        report[employeeId].late6Days += 1;
                    } else if (daysAfterDue >= 7) {
                        report[employeeId].lateWeekOrMore += 1;
                    }
                }
            }
          });
        return report;
      },

      calculatePreviousPeriodMetrics(tasks, startDate, endDate, filterTags = []) {
        let totalTasks = 0;
        let completedTasks = 0;
        let onTimeTasks = 0;
        let incompleteTasks = 0;
        let totalCompletionDays = 0;

        tasks.forEach((task) => {
          const taskDate = moment(task.dueAtStr);

          if (taskDate.isAfter(startDate) && taskDate.isBefore(endDate)) {
            // Apply tag filter
            if (filterTags.length > 0) {
              const taskTags = Array.isArray(task.tags) ? task.tags : [];
              const hasMatchingTag = filterTags.some(filterTag => 
                taskTags.includes(filterTag.name)
              );
              if (!hasMatchingTag) return;
            }
            
            totalTasks++;

            if (task.status !== 'COMPLETED') {
              incompleteTasks++;
            } else {
              completedTasks++;
              const dueDate = new Date(task.dueAtStr);
              const completionDate = new Date(task.completedAtStr);
              const daysAfterDue = Math.floor((completionDate - dueDate) / (1000 * 60 * 60 * 24));

              totalCompletionDays += Math.max(0, daysAfterDue);

              if (daysAfterDue <= 0) {
                onTimeTasks++;
              }
            }
          }
        });

        return {
          totalTasks,
          completedTasks,
          onTimeTasks,
          incompleteTasks,
          totalCompletionDays,
          onTimeRate: completedTasks > 0 ? (onTimeTasks / completedTasks) * 100 : 0,
          avgCompletionTime: completedTasks > 0 ? totalCompletionDays / completedTasks : 0
        };
      },

      setMetricFilter(metric) {
        if (this.selectedMetricFilter === metric) {
          this.selectedMetricFilter = null; // Deselect if already selected
        } else {
          this.selectedMetricFilter = metric;
        }
        this.$nextTick(() => {
          this.renderTaskDistributionChart();
        });
      },

      exportReportToCSV() {
        if (!this.report || Object.keys(this.report).length === 0) {
          alert('No data to export');
          return;
        }

        // Prepare CSV headers
        const headers = [
          'Employee Name',
          'Total Tasks',
          'Completed Tasks',
          'On Time',
          'Late (1 Day)',
          'Late (2 Days)',
          'Late (3 Days)',
          'Late (4 Days)',
          'Late (5 Days)',
          'Late (6 Days)',
          'Late (Over 1 Week)',
          'Incomplete'
        ];

        // Prepare CSV rows
        const csvRows = [headers];
        Object.entries(this.report).forEach(([empId, data]) => {
          const row = [
            this.getEmployeeName(empId),
            data.totalTasks,
            data.completedTasks,
            data.onTime,
            data.late1Day,
            data.late2Days,
            data.late3Days,
            data.late4Days,
            data.late5Days,
            data.late6Days,
            data.lateWeekOrMore,
            data.incomplete
          ];
          csvRows.push(row);
        });

        // Convert to CSV string
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `Task_Report_${moment(this.dateRange[0]).format('YYYY-MM-DD')}_to_${moment(this.dateRange[1]).format('YYYY-MM-DD')}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },

    computed: {

      ...mapState({
          loading: state => state.alert.loading,
          loaded: state => state.alert.loaded,
          errored: state => state.alert.errored,
          alertMessage: state => state.alert.message,
          dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown,
          siteID: state => state.customer.siteID,
          userID: state => state.customer.userID,
          sites: state => state.customer.sites,
          employees: state => state.customer.employees,
          patients: state => state.customer.patients,
          allTasksForSite: state => state.tasks.allTasksForSite
      }),

      chartStyles() {
        return {
          width: '100%',
          height: window.innerWidth < 1400 ? '270px' :  '250px'
        }
      },

      availableTagsInTasks() {
        if (!this.allTasksForSite || this.allTasksForSite.length === 0) return [];
        
        // Extract all unique tags from all tasks (matching main task page approach)
        const uniqueTags = new Set();
        
        this.allTasksForSite.forEach(task => {
          // Use the task.tags array directly (same as main task page)
          if (Array.isArray(task.tags)) {
            task.tags.forEach(tag => {
              if (typeof tag === 'string' && tag.trim() !== '') {
                uniqueTags.add(tag.trim());
              }
            });
          }
        });
        
        // Convert to array of objects for Multiselect
        return Array.from(uniqueTags)
          .sort()
          .map(tag => ({ name: tag }));
      },

      filterSummaryText() {
        if (!this.dateRange || this.dateRange.length !== 2) {
          return 'Showing all tasks';
        }

        const startDate = moment(this.dateRange[0]);
        const endDate = moment(this.dateRange[1]);
        
        // Check if it's the same month/year (e.g., Oct 12-18, 2025)
        if (startDate.format('MMM YYYY') === endDate.format('MMM YYYY')) {
          const monthYear = startDate.format('MMM YYYY');
          const startDay = startDate.format('Do');
          const endDay = endDate.format('Do');
          var summaryText = `Showing all tasks from ${startDay} through ${endDay}, ${monthYear}`;
        } else {
          // Different months/years (e.g., Jan 2025 to Oct 2025)
          const startFormatted = startDate.format('MMM YYYY');
          const endFormatted = endDate.format('MMM YYYY');
          var summaryText = `Showing all tasks from ${startFormatted} to ${endFormatted}`;
        }
        
        if (this.selectedTagsFilter.length > 0) {
          const tagNames = this.selectedTagsFilter.map(t => `"${t.name}"`).join(', ');
          summaryText += ` tagged with ${tagNames}`;
        }
        
        return summaryText;
      },

      sortedScorecardData() {
        if (!this.report || Object.keys(this.report).length === 0) return {};

        const sortedData = Object.entries(this.report).sort((a, b) => {
          const aData = a[1];
          const bData = b[1];
          let comparison = 0;

          if (this.scorecardSortBy === 'totalTasks') {
            comparison = bData.totalTasks - aData.totalTasks;
          } else if (this.scorecardSortBy === 'completionRate') {
            const aRate = this.calculateCompletionRate(aData);
            const bRate = this.calculateCompletionRate(bData);
            comparison = bRate - aRate;
          } else if (this.scorecardSortBy === 'onTimeRate') {
            const aRate = this.calculateOnTimeRate(aData);
            const bRate = this.calculateOnTimeRate(bData);
            comparison = bRate - aRate;
          } else if (this.scorecardSortBy === 'avgCompletionTime') {
            const aTime = this.calculateAvgCompletionTime(aData);
            const bTime = this.calculateAvgCompletionTime(bData);
            comparison = aTime - bTime; // Lower is better, so ascending by default
          }

          return this.scorecardSortOrder === 'desc' ? comparison : -comparison;
        });

        return Object.fromEntries(sortedData);
      },

      // Metrics overview computed properties
      totalTasksInRange() {
        if (!this.report) return 0;

        return Object.values(this.report).reduce((total, employee) => {
          return total + employee.totalTasks;
        }, 0);
      },

      totalCompletedTasksInRange() {
        if (!this.report) return 0;

        return Object.values(this.report).reduce((total, employee) => {
          return total + employee.completedTasks;
        }, 0);
      },

      totalOnTimeTasksInRange() {
        if (!this.report) return 0;

        return Object.values(this.report).reduce((total, employee) => {
          return total + employee.onTime;
        }, 0);
      },

      incompleteTasksCount() {
        if (!this.report) return 0;

        return Object.values(this.report).reduce((total, employee) => {
          return total + employee.incomplete;
        }, 0);
      },

      onTimeCompletionRate() {
        if (this.totalCompletedTasksInRange === 0) return 0;
        return Math.round((this.totalOnTimeTasksInRange / this.totalCompletedTasksInRange) * 100);
      },

      avgCompletionTime() {
        if (!this.report) return 0;

        const totalDays = Object.values(this.report).reduce((total, employee) => {
          return total + employee.totalCompletionDays;
        }, 0);

        if (this.totalCompletedTasksInRange === 0) return 0;
        return (totalDays / this.totalCompletedTasksInRange).toFixed(1);
      },

      onTimeCompletionTrend() {
        if (!this.previousPeriodData) return { text: 'No previous data', icon: 'fa fa-minus', class: '' };

        const prevRate = this.previousPeriodData.onTimeRate;
        const currentRate = this.onTimeCompletionRate;
        const diff = currentRate - prevRate;

        if (Math.abs(diff) < 1) {
          return { text: 'No change', icon: 'fa fa-minus', class: '' };
        } else if (diff > 0) {
          return { text: `+${diff.toFixed(1)}%`, icon: 'fa fa-arrow-up', class: 'trend-up' };
        } else {
          return { text: `${diff.toFixed(1)}%`, icon: 'fa fa-arrow-down', class: 'trend-down' };
        }
      },

      avgCompletionTimeTrend() {
        if (!this.previousPeriodData) return { text: 'No previous data', icon: 'fa fa-minus', class: '' };

        const prevAvg = this.previousPeriodData.avgCompletionTime;
        const currentAvg = parseFloat(this.avgCompletionTime);
        const diff = prevAvg - currentAvg; // Note: for completion time, lower is better

        if (Math.abs(diff) < 0.1) {
          return { text: 'No change', icon: 'fa fa-minus', class: '' };
        } else if (diff > 0) {
          return { text: `${diff.toFixed(1)} days faster`, icon: 'fa fa-arrow-up', class: 'trend-up' };
        } else {
          return { text: `${Math.abs(diff).toFixed(1)} days slower`, icon: 'fa fa-arrow-down', class: 'trend-down' };
        }
      },

      incompleteTrend() {
        if (!this.previousPeriodData) return { text: 'No previous data', icon: 'fa fa-minus', class: '' };

        const prevIncomplete = this.previousPeriodData.incompleteTasks;
        const currentIncomplete = this.incompleteTasksCount;
        const diff = prevIncomplete - currentIncomplete; // Note: for incomplete tasks, lower is better

        if (diff === 0) {
          return { text: 'No change', icon: 'fa fa-minus', class: '' };
        } else if (diff > 0) {
          return { text: `${diff} fewer`, icon: 'fa fa-arrow-up', class: 'trend-up' };
        } else {
          return { text: `${Math.abs(diff)} more`, icon: 'fa fa-arrow-down', class: 'trend-down' };
        }
      },

      makePieChartData2() {
        // Calculate totals across all employees
        if (!this.report || Object.keys(this.report).length === 0) {
          return {
            labels: ['On Time','1 Day Late','2 Days Late','3 Days Late','4 Days Late','5 Days Late','6 Days Late','Over 1 week Late','Incomplete'],
            datasets: [
              {
                label: "Data One",
                // Teamio Huddle-style color palette - consistent across all charts
                backgroundColor: ['#3abf94', '#3b82f6', '#8b5cf6', '#f59e0b', '#64748b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#78716c'],
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0]
              }
            ]
          };
        }

        // Sum up all employee data
        const totals = {
          onTime: 0,
          late1Day: 0,
          late2Days: 0,
          late3Days: 0,
          late4Days: 0,
          late5Days: 0,
          late6Days: 0,
          lateWeekOrMore: 0,
          incomplete: 0
        };

        Object.values(this.report).forEach(employee => {
          totals.onTime += employee.onTime;
          totals.late1Day += employee.late1Day;
          totals.late2Days += employee.late2Days;
          totals.late3Days += employee.late3Days;
          totals.late4Days += employee.late4Days;
          totals.late5Days += employee.late5Days;
          totals.late6Days += employee.late6Days;
          totals.lateWeekOrMore += employee.lateWeekOrMore;
          totals.incomplete += employee.incomplete;
        });

        return {
          labels: ['On Time','1 Day Late','2 Days Late','3 Days Late','4 Days Late','5 Days Late','6 Days Late','Over 1 week Late','Incomplete'],
          datasets: [
            {
              label: "Data One",
              // Teamio Huddle-style color palette - consistent across all charts
              backgroundColor: ['#3abf94', '#3b82f6', '#8b5cf6', '#f59e0b', '#64748b', '#06b6d4', '#ec4899', '#10b981', '#6366f1', '#78716c'],
              data: [
                totals.onTime,
                totals.late1Day,
                totals.late2Days,
                totals.late3Days,
                totals.late4Days,
                totals.late5Days,
                totals.late6Days,
                totals.lateWeekOrMore,
                totals.incomplete
              ]
            }
          ]
        };
      },


    },

    watch: {
      allTasksForSite(newValue) {
        if(newValue.length > 0) {
          // Use the current date range if it exists, otherwise use default
          const startDate = this.dateRange && this.dateRange[0] ? moment(this.dateRange[0]) : moment().startOf('month');
          const endDate = this.dateRange && this.dateRange[1] ? moment(this.dateRange[1]) : moment();

          this.report = this.generateReport(this.allTasksForSite, startDate, endDate, this.selectedTagsFilter);
        }
      },

      selectedTagsFilter: {
        handler(newValue) {
          // Regenerate report when tags change
          this.report = this.generateReport(
            this.allTasksForSite, 
            moment(this.dateRange[0]), 
            moment(this.dateRange[1]),
            newValue
          );
          this.$nextTick(() => {
            this.renderTaskDistributionChart();
          });
        },
        deep: true
      },

      dateRange(newValue) {
        if (newValue && newValue.length === 2) {
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
          }
          this.debounceTimer = setTimeout(() => {
            this.report = this.generateReport(
              this.allTasksForSite, 
              moment(newValue[0]), 
              moment(newValue[1]),
              this.selectedTagsFilter
            );
            this.$nextTick(() => {
              this.renderTaskDistributionChart();
            });
          }, 500);
        }
      },

      report() {
        this.$nextTick(() => {
          this.renderTaskDistributionChart();
        });
      },

      scorecardSortBy() {
        // No need to re-render charts, just update the sorted data
      },

      scorecardSortOrder() {
        // No need to re-render charts, just update the sorted data
      }
    }
  };
</script>

<style scoped lang="scss">
.TaskReports {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  overflow-y: auto;
}

/* Header styling similar to TodoDetails.vue */
.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.export-button {
  background-color: #3abf94;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: background-color 0.2s ease-in-out, transform 0.2s ease;

  &:hover {
    background-color: #2d8c6f;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.3);
  }
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.close-button {
  color: #64748b;
  text-decoration: none;
  cursor: pointer;
  font-size: 1rem;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #3abf94;
  }
}

/* Metrics Overview Styling */
.metrics-overview-container {
  margin-bottom: 1.5rem;
}

.metrics-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

@media (max-width: 768px) {
  .metrics-cards {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}

.metric-card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }

  &.metric-selected {
    border: 2px solid #3abf94;
    box-shadow: 0 4px 6px rgba(58, 191, 148, 0.2);
  }
}

.metric-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: rgba(58, 191, 148, 0.1);
  color: #3abf94;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.25rem;
}

.metric-content {
  flex: 1;
}

.metric-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 0.5rem 0;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
}

.metric-trend {
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &.trend-up {
    color: #10b981;
  }

  &.trend-down {
    color: #ef4444;
  }
}

/* Filter Summary Text */
.filter-summary-container {
  background-color: #f8fafc;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
}

.filter-summary-text {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #3abf94;
  }
}

/* Task Filters Layout (matching main task page) */
.task-filters {
  background-color: transparent;
  border-radius: 8px;
  margin-bottom: 1rem;

  .filters-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: flex-end;
  }

  .filter-group {
    flex: 1;
    min-width: 170px;
    max-width: 250px;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: flex-end;
  }

  .filter-select {
    width: 100%;
  }

  .tag-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.15rem 0;

    i {
      color: #64748b;
      font-size: 0.875rem;
    }
  }

  .clear-filters-button {
    background-color: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-height: 38px;

    &:hover {
      background-color: #dc2626;
    }

    i {
      font-size: 0.75rem;
    }
  }
}

/* Date picker styling - matching main task page exactly */
.task-filters {
  :deep(.mx-datepicker) {
    width: 100%;
    overflow: hidden;
    height: 34px;
    
    .mx-input-wrapper {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      height: 34px;
      background-color: white;
      display: flex;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      
      &:focus-within {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
      }
    }
    
    .filter-date-input {
      border: none;
      background: transparent;
      padding: 4px 8px;
      font-size: 0.8125rem;
      color: #64748b;
      flex: 1;
      min-height: 26px;
      
      &::placeholder {
        color: #9ca3af;
      }
      
      &:focus {
        outline: none;
      }
    }
    
    .mx-input-append {
      padding: 4px 6px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-left: auto;
      
      .mx-calendar-icon {
        width: 14px;
        height: 14px;
        fill: #64748b;
      }
      
      .mx-clear-icon {
        color: #9ca3af;
        font-size: 0.75rem;
        cursor: pointer;
        padding: 2px;
        border-radius: 50%;
        transition: all 0.2s ease;
        
        &:hover {
          color: #ef4444;
          background-color: #fef2f2;
        }
      }
    }
  }
}

/* Multiselect styling - scoped to TaskReports only */
.task-filters {
  :deep(.multiselect) {
    height: 34px;
    .multiselect__tags {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 4px 8px;
      height: 34px;
      background-color: white;
      font-size: 0.8125rem;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      box-sizing: border-box;
      
      &:focus-within {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
      }
    }

    .multiselect__tag {
      background-color: #e5e7eb;
      color: #475569;
      border-radius: 4px;
      padding: 0.15rem 0.4rem 0.15rem 0.5rem;
      margin: 0.15rem;
      font-size: 0.75rem;
      font-weight: 500;
      height: 22px;
      line-height: 1.2;
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      max-width: calc(100% - 10px);
      overflow: hidden;
      text-overflow: ellipsis;

      span {
        max-width: calc(100% - 22px);
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        vertical-align: middle;
      }

      .multiselect__tag-icon {
        line-height: 22px;
        background-color: #d1d5db;
        border-radius: 3px;
        width: 19px;
        height: 19px;
        min-width: 19px;
        margin-left: 5px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #9ca3af;
          color: #ffffff;
        }

        &:after {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 600;
        }
      }
    }

    .multiselect__single {
      font-size: 0.8125rem;
      color: #64748b;
      padding: 0.15rem 0.25rem;
      margin: 0.15rem;
      line-height: 1.2;
      max-width: calc(100% - 10px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .multiselect__placeholder {
      font-size: 0.8125rem;
      color: #9ca3af;
      padding: 0.15rem 0.25rem;
      margin: 0.15rem;
      line-height: 1.2;
    }

    .multiselect__input {
      font-size: 0.8125rem;
      color: #374151;
      padding: 0.15rem 0.25rem;
      margin: 0.15rem;
      line-height: 1.2;
      border: none;
      background: transparent;
    }

    .multiselect__content-wrapper {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    .multiselect__option {
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      color: #374151;

      &:hover {
        background-color: #f3f4f6;
      }

      &.multiselect__option--highlight {
        background-color: #3b82f6;
        color: white;
      }
    }
  }
}

/* Chart container */
.chart-container {
  position: relative;
  height: 250px;
  width: 100%;
}

/* Employee avatars in chart */
.chart-employee-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background-color: #e2e8f0;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.task-details-item {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 0.75rem;
  overflow: visible !important;
  position: relative;
}

.task-details-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.section-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.section-icon {
  margin-right: 0.5rem;
  color: #64748b;
}

.modern-date-input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #334155;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3abf94;
    box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
  }
}

/* Employee reports grid */
.reports-container {
  flex: 1;
  overflow-y: visible;
  margin-bottom: 1.5rem;
}

.employee-reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

@media (max-width: 768px) {
  .employee-reports-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}

.employee-report-card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
}

.employee-card-header {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.employee-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  background-size: cover;
  background-position: center;
  text-transform: uppercase;

  &.bg-blue-100 { background-color: #dbeafe; color: #1e40af; }
  &.bg-green-100 { background-color: #dcfce7; color: #166534; }
  &.bg-yellow-100 { background-color: #fef9c3; color: #854d0e; }
  &.bg-red-100 { background-color: #fee2e2; color: #991b1b; }
  &.bg-indigo-100 { background-color: #e0e7ff; color: #3730a3; }
  &.bg-purple-100 { background-color: #f3e8ff; color: #6b21a8; }
  &.bg-pink-100 { background-color: #fce7f3; color: #9d174d; }
  &.bg-teal-100 { background-color: #ccfbf1; color: #115e59; }
  &.bg-gray-200 { background-color: #e5e7eb; color: #374151; }
}

.employee-name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
}

.chart-panel {
  padding: 1rem;
  display: flex;
  justify-content: center;
}

.scorecard-container {
  max-height: 300px;
  overflow-y: auto;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.performance-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  color: #334155;
}

.performance-table th {
  background-color: #f1f5f9;
  border: 1px solid #e5e7eb;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #0f172a;
  position: sticky;
  top: 0;
  z-index: 1;
}

.performance-table td {
  border: 1px solid #e5e7eb;
  padding: 0.75rem;
}

.performance-table tr:nth-child(even) {
  background-color: #f8fafc;
}

.performance-table tr:hover {
  background-color: #f1f5f9;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.employee-name-cell {
  font-weight: 500;
  color: #0f172a;
}

.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  color: #64748b;
  font-size: 0.9375rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-label {
  font-size: 0.875rem;
  color: #64748b;
}

.form-control {
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #334155;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3abf94;
  }
}

</style>

